import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Box,
  Collapse,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';

const ItemCard = ({ 
  title,
  subtitle = null,
  headerActions = [],
  basicInfo = [],
  expandedContent = null,
  actions = [],
  onClick = null
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  // Responsive padding - μικρότερα για mobile
  const cardPadding = isMobile ? { px: 1, py: 0.75 } : { px: 2, py: 1 };
  const contentPadding = isMobile ? { pt: 0.25, pb: 0.25, px: 1 } : { pt: 0, pb: 1, px: 2 };
  const actionsPadding = isMobile ? { px: 1, pb: 0.75, pt: 0.25 } : { px: 2, pb: 2, pt: 1 };
  const headerPadding = isMobile ? { pb: 0.25, px: 1, pt: 0.75 } : { pb: 1, px: 2, pt: 1.5 };

  return (
    <Card 
      sx={{ 
        boxShadow: 1,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          transform: 'translateY(-1px)',
          boxShadow: 3,
          borderColor: 'primary.main'
        } : {}
      }}
      onClick={onClick}
    >
      {/* Title σε δική του γραμμή */}
      <Box sx={headerPadding}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            fontSize: isMobile ? '0.95rem' : '1rem',
            mb: headerActions.length > 0 || subtitle ? (isMobile ? 0.5 : 0.75) : 0
          }}
        >
          {title}
        </Typography>
        
        {/* Subtitle */}
        {subtitle && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mt: 0.25,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              mb: headerActions.length > 0 ? (isMobile ? 0.5 : 0.75) : 0
            }}
          >
            {subtitle}
          </Typography>
        )}
        
        {/* Header Actions (chips) σε επόμενη γραμμή */}
        {headerActions.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            gap: 0.5, 
            flexWrap: 'wrap',
            mt: subtitle ? 0 : 0.5
          }}>
            {headerActions}
          </Box>
        )}
      </Box>

      {basicInfo.length > 0 && (
        <CardContent sx={contentPadding}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: isMobile ? 0.5 : 1 
          }}>
            {basicInfo.map((info, index) => (
              <Box 
                key={index} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: isMobile ? 0.75 : 1,
                  py: isMobile ? 0.125 : 0
                }}
              >
                {info.icon && (
                  <Box sx={{ 
                    display: 'flex', 
                    fontSize: isMobile ? '0.875rem' : '1rem',
                    minWidth: isMobile ? '1rem' : '1.25rem'
                  }}>
                    {info.icon}
                  </Box>
                )}
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                    lineHeight: isMobile ? 1.3 : 1.5
                  }}
                >
                  {info.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      )}

      {expandedContent && (
        <>
          <Divider />
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <CardContent sx={{ pt: isMobile ? 1 : 2, px: isMobile ? 1 : 2, pb: isMobile ? 1 : 2 }}>
              {expandedContent}
            </CardContent>
          </Collapse>
        </>
      )}

      {(actions.length > 0 || expandedContent) && (
        <CardActions sx={{ justifyContent: 'space-between', ...actionsPadding }}>
          {actions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {actions}
            </Box>
          )}
          
          {expandedContent && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleExpandClick();
              }}
              sx={{ 
                ml: 'auto',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s'
              }}
            >
              ▼
            </IconButton>
          )}
        </CardActions>
      )}
    </Card>
  );
};

export default ItemCard;

